/**
 * features/expenses/hooks.ts
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ExpenseInput } from '@ft/core';
import { api, queryString } from '~/lib/api';
import { queryKeys } from '~/lib/query-client';
import type { Expense, ExpenseCategory, Paginated } from '~/lib/types';

export interface ExpenseListParams {
  page: number;
  pageSize: number;
  search: string;
  categoryId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export function useExpenses(params: ExpenseListParams) {
  return useQuery({
    queryKey: queryKeys.expenses(params),
    queryFn: () =>
      api.get<Paginated<Expense>>(
        `/api/expenses${queryString({
          page: params.page,
          pageSize: params.pageSize,
          search: params.search,
          categoryId: params.categoryId,
          dateFrom: params.dateFrom,
          dateTo: params.dateTo,
        })}`,
      ),
    placeholderData: (previous) => previous,
  });
}

export function useExpense(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.expense(id ?? ''),
    queryFn: () => api.get<Expense>(`/api/expenses/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ExpenseInput) => api.post<Expense>('/api/expenses', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['expenses'] });
      void queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}

export function useUpdateExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ExpenseInput }) =>
      api.put<Expense>(`/api/expenses/${id}`, input),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['expenses'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.expense(variables.id) });
      void queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}

export function useDeleteExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/expenses/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['expenses'] });
      void queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}

// ==================== หมวดหมู่ค่าใช้จ่าย ====================

export function useExpenseCategories() {
  return useQuery({
    queryKey: queryKeys.expenseCategories,
    queryFn: () => api.get<ExpenseCategory[]>('/api/expense-categories'),
    staleTime: 5 * 60_000,
  });
}

export function useCreateExpenseCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.post<ExpenseCategory>('/api/expense-categories', { name }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.expenseCategories });
    },
  });
}

export function useUpdateExpenseCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.put<ExpenseCategory>(`/api/expense-categories/${id}`, { name }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.expenseCategories });
    },
  });
}

export function useDeleteExpenseCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/expense-categories/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.expenseCategories });
    },
  });
}
